/* WARNING!!!
This file is imported both on the server and the client!
Make sure all dependencies are context-agnostic. */
import { Response as NodeFetchResponse } from "node-fetch";
import { Dropbox, files } from "dropbox";
import {
  AUDIO_QUALITY,
  audioQualityOptions,
  Comment,
  COMMENT_BLOCK_TYPE,
  Entity,
  Id,
  Space,
  SpaceTrackRow,
  Track,
  SUBSCRIPTION_TIER,
  FileBlockV2,
  FileEntity,
  STRIPE_INTERVAL_FORMATTED,
  SUBSCRIPTION_INTERVAL,
  SUBSCRIPTION_PRICING,
  SUBSCRIPTION_TIER_FORMATTED,
  SUBSCRIPTION_TIER_HIERARCHY,
  UserSubscriptions,
  ROLE,
  COMPATIBLE_ROLES,
  PUBLIC_ID,
  COLLECTION_ID,
  UserEntity,
  SHARE_KEY_REGEX,
  AUDIO_ACCEPT_TYPES,
  SHARE_KEY_PREFIX,
  DropboxSyncJob,
  DropboxAuthTokens,
  RevenueCatEntitlement,
  REVENUECAT_PRODUCT_ID_TO_SUBSCRIPTION,
  REVENUECAT_PRODUCT_PROVIDER,
} from "./entities";
import { Stripe } from "stripe";
import { APP_FEATURES, AppFeaturesStatus } from "./features";
import { JOB_STATUS } from "./entities";
/* These come from `./roles`, a leaf module with no imports of its own, and
 * not from `./entities`. This file and `entities.ts` already form a require
 * cycle (each imports the other), and `roles.ts` is the one module both of
 * them can depend on without a deeper cycle. */
import { INHERITED_ADMIN_ROLES, INVITE_KEY_PREFIX } from "./roles";

// No import support for Node
// eslint-disable-next-line @typescript-eslint/no-var-requires
const moment = require("moment");
// No TS support

export const ADMIN_UID = "auth0|62cf2b879d94d0fae1cfada1";

export const MAX_PINNED_VERSIONS = 3;
export const MAX_PINNED_VERSIONS_FREE = 2;

export const MAX_TITLE_LENGTH = 50;
export const MAX_DESCRIPTION_LENGTH = 300;

/**
 * The length cap on `spaces-v1.itemsOrder`.
 *
 * This guard exists for availability, not for integrity: it stops writes
 * that make the space document too large to update, and it does not
 * validate the content. `updateSpace` writes `itemsOrder` with no
 * `checkAllowed*` of its own. So any authenticated caller can write an
 * array of any length to any space with only its id, and space ids are
 * not secret. That includes the anonymous session the web client creates
 * on every page load with no login. `itemsOrder` is exempt from indexes,
 * so the 40,000-entry index limit is not the limit that binds. The 1 MiB
 * document limit is.
 * Past it, every write that grows the space document fails with
 * INVALID_ARGUMENT: membership grants, renames, archive, `memberCount`,
 * `membersRev`.
 *
 * The value comes from a measurement; it is no longer the placeholder. A
 * survey of `itemsOrder` lengths over the complete dev data set (3,829
 * spaces) found only 49 spaces with a non-empty `itemsOrder`. The
 * percentiles over the whole set are therefore p50 0, p95 0, p99 3. "Set
 * it from the measured p99" was the wrong instruction, because a cap of 3
 * is nonsense. The two usable numbers are the maximum, 571, and the count
 * of spaces already over each candidate cap: 500 puts 1 space over;
 * 1,000, 2,500, 5,000, 10,000 and 20,000 each put 0 spaces over.
 *
 * 2,500 comes from that distribution, in both directions:
 *  - Not lower. At 500, exactly one real space (571 entries) is already
 *    over. The growth-only form below means that space is not stuck: a
 *    caller can still write it shorter. But no caller can ever write it
 *    longer, and that is a behaviour change on live data with no security
 *    argument behind it. 2,500 is 4.4 times today's largest space. The
 *    largest real space can therefore grow past four times its current
 *    size before anyone sees the error.
 *  - Not higher. At about 36 bytes per id, 2,500 entries is about 90 KB,
 *    two orders of magnitude below the 1 MiB document limit this guard
 *    defends. 20,000 permits about 720 KB in one call, so it does not stop
 *    the attack this guard exists for: one call that fills the document.
 *    The survey note says plainly that the placeholder must never ship.
 *
 * WARNING: Re-check this cap against a production run of the same survey
 * before a production rollout. The numbers above come from the dev
 * project, and nobody measured the production distribution:
 * `migrate/spaceMembership/0-census.js` cannot run against the production
 * project by design. If a production space is already over 2,500, its
 * owner can still repair it downward, but it can no longer grow. Someone
 * must measure that and decide first; a user must not discover it as a
 * surprise. `shared-util.test.ts` pins this value, so a change to it is a
 * deliberate edit, not a silent one.
 */
export const MAX_SPACE_ITEMS_ORDER = 2_500;

export const MAX_SPACE_ITEMS_ORDER_ERROR =
  "This space has too many items to reorder.";

/**
 * The growth-only form of the cap. A caller can still make a space's
 * stored `itemsOrder` shorter when it already exceeds
 * `MAX_SPACE_ITEMS_ORDER`. Without that allowance, the guard blocks every
 * reorder on every space above the cap. The intent is only to stop
 * growth.
 *
 * `currentLength` is the length of the stored array, not of the payload.
 */
export const isItemsOrderWithinCap = (
  nextLength: number,
  currentLength: number,
) => nextLength <= Math.max(currentLength || 0, MAX_SPACE_ITEMS_ORDER);

export const DEMO_ENTITY_PREFIX = "debut-";

export const TYPEFORM_WELCOME_SURVEY_ID = "RqkZVKoB";

export const AUDIO_UPLOAD_SIZE_LIMIT_IN_BYTES = 2 * 1024 * 1024 * 1024; // 2GB

const ENTITY_PROPS = [
  "id",
  "createdBy",
  "lastUpdatedBy",
  "createdAt",
  "updatedAt",
  "rolesV2",
  "inheritedRolesV3",
  "readableByV2",
];

export interface AuthToken {
  entityPasswords?: Record<
    string,
    {
      share?: string;
      public?: string;
    }
  >;
  shareKey?: string;
}

export const getFullRoles = (entity: Entity) => {
  if (!entity) return {};

  const inheritedRoles = entity.inheritedRolesV3 || {};
  const entityRoles = entity.rolesV2 || {};

  /* Seed from the inherited map, with a copy of each array rather than the
   * array itself. A plain `{ ...inheritedRoles }` hands the caller's own
   * arrays back out, so any code that mutates the returned map corrupts
   * `entity.inheritedRolesV3`. Non-array values (`null`, and anything
   * malformed) pass through untouched, so the rest of this function behaves
   * exactly as before. */
  const allRoles: Entity["rolesV2"] = {};
  Object.entries(inheritedRoles).forEach(([userId, permissions]) => {
    allRoles[userId] = Array.isArray(permissions)
      ? [...permissions]
      : permissions;
  });

  // Anyone who owns the parent collection is downgraded to Manage
  Object.entries(inheritedRoles).forEach(([userId, permissions]) => {
    /* `permissions || []` makes a `null` or `undefined` value in
     * `inheritedRolesV3` safe. Without it, a single `null` value throws
     * `TypeError: Cannot read properties of null (reading 'includes')`,
     * and every membership write and every render of that space then fails
     * with a 500 error. */
    if ((permissions || []).includes(ROLE.ADMIN)) {
      allRoles[userId] = [...INHERITED_ADMIN_ROLES];
    }
  });

  Object.entries(entityRoles).forEach(([userId, userRoles]) => {
    // Anyone who has management access to the parent should at least have view
    // access, so they can remove it if they want.
    const canManageParent =
      allRoles[userId] &&
      (allRoles[userId].includes(ROLE.ADMIN) ||
        allRoles[userId].includes(ROLE.MANAGE));

    if (userRoles) {
      /* Copy before the push below. If this code assigns `userRoles`
       * directly and then pushes into it, it mutates
       * `entity.rolesV2[userId]` in place: the caller's entity gains a
       * role that nobody granted, and the returned map shares the entity's
       * own arrays. */
      const directRoles = Array.isArray(userRoles) ? [...userRoles] : userRoles;
      if (canManageParent && !userRoles.includes(ROLE.VIEW)) {
        directRoles.push(ROLE.VIEW);
      }
      allRoles[userId] = directRoles;
    }
  });

  if (entity.createdBy) {
    allRoles[entity.createdBy] = [ROLE.ADMIN];
  }

  return allRoles;
};

export enum PERMISSION {
  TO_CLONE_TRACK,
  TO_DELETE_SPACE,
  TO_ARCHIVE_SPACE,
  TO_MANAGE_SPACE,
  TO_ADD_TO_SPACE,
  TO_ADD_SPACE_TO_SPACE,
  TO_ADD_ATTACHMENT_TO_SPACE,
  TO_VIEW_SPACE,
  TO_REMOVE_SELF_FROM_SPACE,

  TO_TOGGLE_SPACE_CHAT_ENABLED_FIELD,
  TO_TOGGLE_SPACE_SPACE_SETUP_FIELD,

  TO_REMOVE_FILE_FROM_TRACK_IN_SPACE,
  TO_DOWNLOAD_TRACK,
  TO_DOWNLOAD_TRACK_IN_SPACE,
  TO_ADD_TO_TRACK,
  TO_ADD_TO_TRACK_IN_SPACE,
  TO_EDIT_TRACK_IN_SPACE,
  TO_EDIT_SPACE_TRACK,
  TO_VIEW_TRACK_IN_SPACE,

  TO_ADD_PINNED_TRACK_VERSION,

  TO_RESOLVE_COMMENT_IN_SPACE,
  TO_DELETE_COMMENT_IN_SPACE,
  TO_EDIT_COMMENT_IN_SPACE,
  TO_COMMENT_IN_SPACE,
  TO_REPLY_TO_COMMENT_IN_SPACE,
  TO_REACT_TO_COMMENT_IN_SPACE,

  TO_DISABLE_DOWNLOAD,
  TO_DISABLE_GUEST_MODE,
  TO_DOWNLOAD_SPACE,
  TO_DOWNLOAD_FILE_IN_SPACE,
  TO_DOWNLOAD_FILE_IN_COMMENT,
  TO_CREATE_FILE_IN_SPACE,
  TO_EDIT_FILE,

  TO_LOCK_AUDIO_QUALITY,
}

/**
 * The member-document answer for one (space, identity) pair. This type is
 * the switch point where a later step of the migration moves reads from
 * the legacy fields to member documents.
 *
 * WARNING: Every method can return `undefined`, and `undefined` means "I
 * cannot answer". It never means "no access". On `undefined` the caller
 * falls back to the legacy in-document role map. That fallback stays
 * correct through the migration because the server writes the legacy
 * fields in every mode (see `SpaceMembershipMode` in `features.ts`). That
 * property is what makes the switch safe. A rules mistake, a denied
 * collection-group `list`, or a stream that did not load yet all degrade
 * to today's behaviour, not to an empty app. (A collection-group `list`
 * is a query across every `members-v1` subcollection.)
 *
 * `getEffectiveRoles` returns the identity's roles on that space, with the
 * space's public roles already merged in. `hasRole` merges
 * `roles[PUBLIC_ID]` into every identity's role set, so a resolver that
 * omits the public merge silently grants too little on every public
 * space. The resolver owns that merge precisely because it receives a
 * `spaceId` and not an entity.
 *
 * WARNING: Use the resolver for spaces only. Callers also pass tracks,
 * comments and files to `hasRole`, and a resolver asked about `track.id`
 * as if it were a `spaceId` answers a question about a different
 * document. `isAllowed`'s `can()` enforces this: it passes the resolver
 * only when the entity is `entities.space`. A direct
 * `hasRole(id, role, entity, resolver)` call must uphold the same rule
 * itself.
 */
export type MembershipResolver = {
  getEffectiveRoles: (props: {
    identifier: Id;
    spaceId: Id;
  }) => ROLE[] | undefined;
  getIsTopLevel: (props: {
    identifier: Id;
    spaceId: Id;
  }) => boolean | undefined;
};

export const hasRole = (
  identifier: string,
  role: ROLE,
  entity: Entity,
  resolver?: MembershipResolver,
) => {
  const userId = sanitizeShareKey(identifier);
  const rolesToMatch = COMPATIBLE_ROLES[role] || [];

  /* Every caller today passes no resolver, and so does every caller below
   * the new read model. With no resolver this whole block does nothing,
   * and the three lines below are byte-identical to what they were before
   * this migration started. */
  const resolved = entity?.id
    ? resolver?.getEffectiveRoles({ identifier: userId, spaceId: entity.id })
    : undefined;
  if (resolved) {
    /* Not merged with `roles[PUBLIC_ID]` again: the resolver already did
     * that. */
    return resolved.some((r) => rolesToMatch.includes(r));
  }

  const roles = getFullRoles(entity);
  const userRoles = [...(roles[userId] || []), ...(roles[PUBLIC_ID] || [])];
  return userRoles.some((r) => rolesToMatch.includes(r));
};

export const isAllowed: (
  key: PERMISSION,
  identifier: Id,
  entities: {
    space?: Space;
    comment?: Comment;
    track?: Track;
    file?: FileEntity;
    spaceTrackRow?: SpaceTrackRow;
  },
  resolver?: MembershipResolver,
) => boolean = (key, identifier, entities, resolver) => {
  const userId = sanitizeShareKey(identifier);
  const can = (role: ROLE, entity: Entity) => {
    if (userId === ADMIN_UID) return true;
    if (!entity) return false;
    /* The resolver answers questions about spaces. If this code hands it
     * a track, a comment or a file, the resolver looks up `members-v1`
     * under an id that is not a space id. Reference equality is the right
     * test here: these are the same objects the caller passed in. */
    return hasRole(
      userId,
      role,
      entity,
      entity === entities.space ? resolver : undefined,
    );
  };

  if (key === PERMISSION.TO_CLONE_TRACK) {
    return (
      // you can either be a manager of the track's space
      can(ROLE.MANAGE, entities.space) ||
      // or both own the track AND able to upload to the space
      (can(ROLE.ADMIN, entities.track) && can(ROLE.UPLOAD, entities.space))
    );
  }

  if (key === PERMISSION.TO_TOGGLE_SPACE_CHAT_ENABLED_FIELD) {
    return !isShareId(userId) && can(ROLE.MANAGE, entities.space);
  }

  if (key === PERMISSION.TO_TOGGLE_SPACE_SPACE_SETUP_FIELD) {
    return !isShareId(userId) && can(ROLE.ADMIN, entities.space);
  }

  if (key === PERMISSION.TO_DISABLE_GUEST_MODE) {
    if (
      !entities.space?.subscriptionTier ||
      entities.space?.subscriptionTier === SUBSCRIPTION_TIER.FREE
    ) {
      return false;
    }
    const baseAllowed = can(ROLE.MANAGE, entities.space);
    const isPrivateInbox = isSpacePrivateInboxEnabled(entities.space);
    return !isPrivateInbox && baseAllowed;
  }

  if (key === PERMISSION.TO_DISABLE_DOWNLOAD && entities.space) {
    const entity = entities.space;
    const baseAllowed = can(ROLE.MANAGE, entity);
    const entityAllowed =
      entity.subscriptionTier &&
      entity.subscriptionTier !== SUBSCRIPTION_TIER.FREE;
    return baseAllowed && entityAllowed;
  }

  if (key === PERMISSION.TO_LOCK_AUDIO_QUALITY) {
    const entity = entities.space;
    const entityAllowed = entity?.subscriptionTier === SUBSCRIPTION_TIER.STUDIO;
    return entityAllowed && can(ROLE.MANAGE, entity);
  }

  /* SPACE */
  if (key === PERMISSION.TO_DELETE_SPACE) {
    return (
      entities.space &&
      !entities.space.isDraftSpace &&
      can(ROLE.ADMIN, entities.space)
    );
  }

  if (key === PERMISSION.TO_ARCHIVE_SPACE) {
    return (
      entities.space &&
      !entities.space.isDraftSpace &&
      can(ROLE.ADMIN, entities.space)
    );
  }

  if (key === PERMISSION.TO_MANAGE_SPACE) {
    return !entities.space?.isArchived && can(ROLE.MANAGE, entities.space);
  }

  if (key === PERMISSION.TO_ADD_TO_SPACE) {
    return !entities.space?.isArchived && can(ROLE.UPLOAD, entities.space);
  }

  if (
    key === PERMISSION.TO_ADD_SPACE_TO_SPACE ||
    key === PERMISSION.TO_ADD_ATTACHMENT_TO_SPACE
  ) {
    // if checking for TO_ADD_SPACE_TO_SPACE and the space is password protected,
    // return false
    if (
      key === PERMISSION.TO_ADD_SPACE_TO_SPACE &&
      entities.space?.sharePasswordEnabled
    ) {
      return false;
    }
    const isPrivateInboxEnabled = isSpacePrivateInboxEnabled(entities.space);
    // if a private inbox is ON, you have to be logged-in to be able to add a space to a space
    if (isPrivateInboxEnabled && isShareId(userId)) {
      return false;
    }
    return isAllowed(
      PERMISSION.TO_ADD_TO_SPACE,
      identifier,
      entities,
      resolver,
    );
  }

  if (key === PERMISSION.TO_VIEW_SPACE) {
    return can(ROLE.VIEW, entities.space);
  }

  if (key === PERMISSION.TO_REMOVE_SELF_FROM_SPACE) {
    const isOwner = entities.space?.createdBy === userId;
    /* `member.isTopLevel` is an exact mirror of "`memberId` is in
     * `space.topLevelReadableBy`".
     *
     * `??`, not `||`. A resolved `false` is an answer, and it must beat
     * the legacy array. `||` falls through to the legacy array and hands
     * "Remove Myself" back to someone the member document says is not a
     * root member. */
    const resolvedTopLevel = entities.space?.id
      ? resolver?.getIsTopLevel({
          identifier: userId,
          spaceId: entities.space.id,
        })
      : undefined;
    const isTopLevelReadable =
      resolvedTopLevel ?? entities.space?.topLevelReadableBy?.includes(userId);
    return !isOwner && isTopLevelReadable;
  }

  /* TRACK IN SPACE */
  if (key === PERMISSION.TO_REMOVE_FILE_FROM_TRACK_IN_SPACE) {
    return (
      can(ROLE.ADMIN, entities.file) ||
      can(ROLE.ADMIN, entities.track) ||
      can(ROLE.MANAGE, entities.space)
    );
  }

  if (key === PERMISSION.TO_DOWNLOAD_TRACK) {
    return can(ROLE.ADMIN, entities.track);
  }

  if (key === PERMISSION.TO_DOWNLOAD_TRACK_IN_SPACE) {
    return (
      can(ROLE.DOWNLOAD, entities.space) || can(ROLE.ADMIN, entities.track)
    );
  }

  // this is for tracks that do not belong to any space
  if (key === PERMISSION.TO_ADD_TO_TRACK) {
    return (
      entities.track &&
      !entities.track?.spaceId &&
      can(ROLE.ADMIN, entities.track)
    );
  }

  if (key === PERMISSION.TO_ADD_TO_TRACK_IN_SPACE) {
    return can(ROLE.MANAGE, entities.space) || can(ROLE.UPLOAD, entities.space);
  }

  if (key === PERMISSION.TO_ADD_PINNED_TRACK_VERSION) {
    // Check if the space owner is on the FREE tier and the track has exceeded the max pinned versions
    if (
      (!entities.space?.subscriptionTier ||
        entities.space?.subscriptionTier === SUBSCRIPTION_TIER.FREE) &&
      (entities.track?.pinnedVersionFiles?.length || 0) >=
        MAX_PINNED_VERSIONS_FREE
    ) {
      return false;
    }

    // Check if the user has MANAGE or UPLOAD permissions
    return can(ROLE.MANAGE, entities.space) || can(ROLE.UPLOAD, entities.space);
  }

  if (key === PERMISSION.TO_EDIT_TRACK_IN_SPACE) {
    return can(ROLE.ADMIN, entities.track) || can(ROLE.MANAGE, entities.space);
  }

  /* Edit ONE space's alias of a track (its per-space title, description,
   * artist name, artwork). Decision 8 (2026-09-07): the author, a manager of
   * that space, or an uploader of that space who is the one who added the
   * track there. `addedBy` is on the row, so the row must be supplied; with
   * no row the uploader arm cannot be proven and is denied. `userId` here is
   * already share-key-sanitized, and a share key never equals an `addedBy`
   * uid, so a share-link visitor never passes the uploader arm. */
  if (key === PERMISSION.TO_EDIT_SPACE_TRACK) {
    return (
      can(ROLE.ADMIN, entities.track) ||
      can(ROLE.MANAGE, entities.space) ||
      (can(ROLE.UPLOAD, entities.space) &&
        !!entities.spaceTrackRow &&
        entities.spaceTrackRow.addedBy === userId)
    );
  }

  if (key === PERMISSION.TO_VIEW_TRACK_IN_SPACE) {
    return can(ROLE.ADMIN, entities.track) || can(ROLE.VIEW, entities.space);
  }

  /* COMMENT IN SPACE */
  if (key === PERMISSION.TO_RESOLVE_COMMENT_IN_SPACE) {
    return can(ROLE.ADMIN, entities.space) || can(ROLE.MANAGE, entities.space);
  }

  if (key === PERMISSION.TO_DELETE_COMMENT_IN_SPACE) {
    return (
      (entities.space &&
        !entities.space.isDraftSpace &&
        can(ROLE.MANAGE, entities.space)) ||
      (can(ROLE.ADMIN, entities.comment) && can(ROLE.COMMENT, entities.space))
    );
  }

  if (key === PERMISSION.TO_EDIT_COMMENT_IN_SPACE) {
    return (
      !entities.comment?.isResolved &&
      can(ROLE.ADMIN, entities.comment) &&
      can(ROLE.COMMENT, entities.space)
    );
  }

  if (key === PERMISSION.TO_COMMENT_IN_SPACE) {
    return !entities.space?.isArchived && can(ROLE.COMMENT, entities.space);
  }

  if (key === PERMISSION.TO_REPLY_TO_COMMENT_IN_SPACE) {
    return (
      !entities.space?.isArchived &&
      !entities.comment?.isResolved &&
      can(ROLE.REPLY, entities.space)
    );
  }

  if (key === PERMISSION.TO_REACT_TO_COMMENT_IN_SPACE) {
    return !entities.space?.isArchived && can(ROLE.REACT, entities.space);
  }

  if (key === PERMISSION.TO_DOWNLOAD_SPACE) {
    return can(ROLE.DOWNLOAD, entities.space);
  }

  /* FILE IN SPACE */
  if (key === PERMISSION.TO_DOWNLOAD_FILE_IN_SPACE) {
    return (
      can(ROLE.DOWNLOAD, entities.space) ||
      !entities.file ||
      can(ROLE.ADMIN, entities.file)
    );
  }

  if (key === PERMISSION.TO_DOWNLOAD_FILE_IN_COMMENT) {
    return can(ROLE.ADMIN, entities.comment);
  }

  if (key === PERMISSION.TO_CREATE_FILE_IN_SPACE) {
    return can(ROLE.UPLOAD, entities.space);
  }

  /* FILE */
  if (key === PERMISSION.TO_EDIT_FILE) {
    return can(ROLE.ADMIN, entities.file);
  }

  return false;
};

export const isInviteId = (userId = "") => {
  return userId?.startsWith(INVITE_KEY_PREFIX);
};

/**
 * Compute the canonical `memberIds` array for a space from its `rolesV2`
 * map and `createdBy` field.
 *
 * - Excludes share-link tokens (`share_*`) — not real users.
 * - Excludes pending email invites (`invite:<email>`) — placeholder ids
 *   for users who haven't accepted yet.
 * - Always includes the space creator's id (the creator is logically a
 *   member of their own space, even if they are not in `rolesV2`).
 * - Returns a de-duplicated, alphabetically sorted array.
 */
export const computeSpaceMemberIds = (
  rolesV2: Space["rolesV2"],
  createdBy?: string,
): string[] =>
  Array.from(
    new Set([
      ...Object.keys(rolesV2 || {}).filter(
        (key) => !key.startsWith(SHARE_KEY_PREFIX) && !isInviteId(key),
      ),
      ...(createdBy ? [createdBy] : []),
    ]),
  ).sort();

// shareKey was once in a format that started with "share|", which caused issues
// due to how "|" is not safe when used in URL. So, we changed the format to start
// with "share_".
// sanitizeShareKey converts shareKey in the old format to the new format.
export const sanitizeShareKey = (shareKey: string) => {
  return shareKey && shareKey.replace(SHARE_KEY_REGEX, SHARE_KEY_PREFIX);
};

export const isShareId = (userId: string) => {
  return userId && SHARE_KEY_REGEX.test(userId);
};

export const getInviteEmail = (userId = "") => {
  const parts = userId.split(INVITE_KEY_PREFIX);
  return parts[1]?.replace("\\", ".");
};

export const getCommentType = (comment: Comment) => {
  const block = comment?.blocks && comment.blocks[0];
  let commentType: string = block?.type;
  if (block?.type === "custom") commentType = "sticker";
  if (block?.type === "file-v2") commentType = "voice";
  return commentType;
};

/**
 * Whether a comment is a reaction — by explicit `commentType` or by its first
 * block's type. Reactions never fill a track's featured slot and are excluded
 * from reply counts and the recent-commenter avatar stack. Shared by the
 * client and the server.
 */
export const isReactionComment = (comment: Comment | undefined): boolean => {
  if (!comment) return false;
  if (comment.commentType === COMMENT_BLOCK_TYPE.REACTION) return true;
  return comment.blocks?.[0]?.type === COMMENT_BLOCK_TYPE.REACTION;
};

/**
 * Whether a comment is eligible for a track's featured slot as text: a
 * top-level, non-reaction comment whose first block is non-empty text.
 */
export const isTextComment = (comment: Comment): boolean => {
  if (comment.parentComment) return false;
  if (isReactionComment(comment)) return false;
  const block = comment.blocks?.[0];
  if (!block || block.type !== COMMENT_BLOCK_TYPE.TEXT) return false;
  return (block.text || "").trim().length > 0;
};

/** The file id of a voice (file-v2) comment's first block, or null. */
export const voiceCommentFileId = (comment: Comment): string | null => {
  const block = comment.blocks?.[0];
  if (!block || block.type !== COMMENT_BLOCK_TYPE.FILE) return null;
  return (block as FileBlockV2).file || null;
};

/**
 * Whether a comment is a voice note: a top-level, non-reaction comment whose
 * first block is a file-v2. Shape check only — whether the underlying file is
 * processed is enforced separately by callers that need a *highlightable*
 * voice note.
 */
export const isVoiceComment = (comment: Comment): boolean => {
  if (comment.parentComment) return false;
  if (isReactionComment(comment)) return false;
  return comment.blocks?.[0]?.type === COMMENT_BLOCK_TYPE.FILE;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const sanitizeEntityData = (data: any) => {
  const sanitized: any = {};
  /* eslint-enable @typescript-eslint/no-explicit-any */

  Object.keys(data).forEach((key) => {
    const parts = key.split(",");
    if (parts[0] === "rolesV2" && !!parts[1]) {
      sanitized[`rolesV2,${parts[1].toLowerCase()}` as string as keyof Entity] =
        data[key];
      return;
    }

    if (!ENTITY_PROPS.includes(key)) {
      return;
    }

    sanitized[key as keyof Entity] = data[key];
  });

  return sanitized as Entity;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const sanitizeCommentData = <T>(data: any) => {
  const sanitized: any = { ...sanitizeEntityData(data) };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  Object.keys(data).forEach((key) => {
    if (
      ![
        "spaceId",
        "trackId",
        "timestamp",
        "socialAssets",
        "duration",
        "replies",
        "blocks",
        "parentComment",
        "createdByAlias",
        "trackVersionIds",
        "isResolved",
      ].includes(key) &&
      !key.startsWith("socialAssets,")
    ) {
      return;
    }

    sanitized[key as keyof T] = data[key];
  });

  if (sanitized.blocks?.[0]?.type) {
    sanitized.commentType = sanitized.blocks[0].type;
  }

  return sanitized as T;
};

export const stripExtension = (path: string) => path.replace(/\.[^/.]+$/, "");

export const isAIFPath = (path: string) => path?.match(/\.(aif|aiff)$/);
export const isUniversalAudioPath = (path: string) =>
  path?.match(/\.(wav|mp3|mp4)$/);

export const formatDuration = (time: Seconds, roundUp?: boolean) => {
  const _min = Math.floor(time / 60);
  let _sec = Math.floor(time % 60);

  if (roundUp && _min === 0 && _sec === 0) {
    // EDGE CASE!
    // If the duration is teeny tiny (less than 1 sec)
    // Round up to 1 second.
    if (time > 0) _sec = 1;
  }

  const min = isNaN(_min) ? 0 : _min;
  const sec = isNaN(_sec) ? 0 : _sec;
  const secString = sec < 10 ? `0${sec}` : String(sec);
  return `${min}:${secString}`;
};

export const formatDatetime = (unix: number) => {
  return moment.unix(unix).format("MMMM Do [at] h:mm a");
};

export const defaultUploadFileName = "original-file";

export const getFileStoragePath = ({
  userId,
  fileId,
  fileName,
}: {
  userId: string;
  fileId: string;
  fileName: string;
}) => {
  const ext = getFileExtension(fileName);
  return `${userId}/${fileId}/${defaultUploadFileName}.${ext}`;
};

export const getUserIdFromStoragePath = (path: string) => path.split("/")[0];

export const parsePriceId = (priceId: string | null) => {
  let interval: SUBSCRIPTION_INTERVAL;
  let tier: SUBSCRIPTION_TIER = SUBSCRIPTION_TIER.FREE;

  if (!priceId) return { tier, interval };

  [
    SUBSCRIPTION_TIER.INDIE,
    SUBSCRIPTION_TIER.PRO,
    SUBSCRIPTION_TIER.STUDIO,
  ].forEach((tierKey) => {
    Object.entries(SUBSCRIPTION_PRICING[tierKey]).forEach(
      ([intervalKey, id]) => {
        if (id === priceId) {
          interval = intervalKey as SUBSCRIPTION_INTERVAL;
          tier = tierKey;
        }
      },
    );
  });

  return { tier, interval };
};

type ParsedSubscription = {
  tier: SUBSCRIPTION_TIER;
  priceId: string | null;
  interval: SUBSCRIPTION_INTERVAL;
};

export const parseSubscription = (
  subscription?: Stripe.Subscription,
): ParsedSubscription => {
  const subItems = subscription?.items?.data || [];
  const item = subItems[0];
  const priceId = item?.price?.id || null;
  const { tier, interval } = parsePriceId(priceId);
  return {
    tier,
    priceId,
    interval,
  };
};

export const parseScheduledSubscription = (
  scheduledSubscription?: Stripe.SubscriptionSchedule,
): ParsedSubscription => {
  const subItems = scheduledSubscription?.phases[0]?.items || [];
  const item = subItems[0];
  const priceId = item?.price as string;
  const { tier, interval } = parsePriceId(priceId);
  return {
    tier,
    priceId,
    interval,
  };
};

export const getFileBlocksFromComment = (comment: Comment) => {
  const commentBlocks = comment.blocks || [];
  const fileBlocks: FileBlockV2[] = [];
  commentBlocks.forEach((b) => {
    if (b.type === "file-v2") fileBlocks.push(b as FileBlockV2);
    if (b.type === "poll-v2") {
      b.options.forEach((option) => {
        if (option.type === "file-v2") fileBlocks.push(option as FileBlockV2);
      });
    }
  });

  return fileBlocks;
};

export const processWaveformData = (_waveformData: number[], numBars = 50) => {
  let waveformData = _waveformData;
  if (!waveformData || waveformData.length === 0) {
    waveformData = [0];
  }

  const groups = new Array(numBars).fill("").map(() => [] as number[]);

  let data: number[] = waveformData;

  if (waveformData.length < numBars) {
    data = [...groups].map((_, i) => {
      const ratio = i / groups.length;
      const waveformIndex = Math.floor(ratio * waveformData.length);
      return waveformData[waveformIndex];
    }) as number[];
  }

  const scale = data.length / groups.length;
  data.forEach((datum, i) => {
    const groupIndex = Math.floor(i / scale);
    groups[groupIndex].push(datum);
  });

  const averages = new Array(numBars).fill(0);
  groups.forEach((group, i) => {
    const sum = group.reduce((s: number, num: number) => s + num, 0);
    const avg = sum / group.length;
    averages[i] = avg;
  });

  const max = averages.reduce((n, a) => (a > n ? a : n), -100);
  const min = averages.reduce((n, a) => (a < n ? a : n), 0);
  const range = max - min;

  const ratio = (n: number) => {
    const val = Math.min(Math.max((n - min) / range, 0.01), 1);
    return isNaN(val) ? 0 : val;
  };

  return averages.map(ratio);
};

export const getSubscriptionTier = (subscriptions: UserSubscriptions) => {
  const { active } = subscriptions;
  if (!active) return SUBSCRIPTION_TIER.FREE;

  const subscriptionItem = active.items.data[0];
  const priceId = subscriptionItem.price.id;
  const { tier } = parsePriceId(priceId);
  return tier;
};

// WARNING!!!!
// If you edit these values, you must edit the matching values in
// @highnote/server/storage.rules
export const getStorageLimitByTier = (
  tier: SUBSCRIPTION_TIER = SUBSCRIPTION_TIER.FREE,
) => {
  const isProd = process.env.DEPLOY_ENV === "production";

  if (tier === SUBSCRIPTION_TIER.STUDIO) {
    return 1024 * 1024 * 1024 * 1024 * 5; // 5 TB - basically unlimited
  }

  if (tier === SUBSCRIPTION_TIER.INDIE || tier === SUBSCRIPTION_TIER.PRO) {
    if (isProd) return 1024 * 1024 * 1024 * 1024; // 1 TB
    return 1024 * 1024 * 20; // 20 MB
  }

  if (isProd) return 1024 * 1024 * 1024 * 50; // 50 GB
  return 1024 * 1024 * 10; // 10 MB
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getActiveSpacesLimitByTier = (tier: SUBSCRIPTION_TIER) => {
  return Infinity;
  // we are allowing unlimited number of active spaces for all users as part of
  // monetization v3, but keeping this code here in case we need to limit it in the future
  // if (tier === SUBSCRIPTION_TIER.STUDIO || tier === SUBSCRIPTION_TIER.PRO) {
  //   return Infinity;
  // }

  // if (tier === SUBSCRIPTION_TIER.INDIE) {
  //   return 3;
  // }

  // return 1;
};

export const getTrackVersionsLimitByTier = (
  tier: SUBSCRIPTION_TIER = SUBSCRIPTION_TIER.FREE,
) => {
  if (tier === SUBSCRIPTION_TIER.STUDIO) {
    return Infinity;
  }

  if (tier === SUBSCRIPTION_TIER.PRO) {
    return 1500;
  }

  if (tier === SUBSCRIPTION_TIER.INDIE) {
    return 500;
  }

  if (process.env.DEPLOY_ENV === "production") {
    return 15;
  }
  // for development, we limit to 5 versions for easier testing
  return 5;
};

export const parseFileSize = (file: FileEntity) => {
  const size = Number(file.size);
  return !isNaN(size) ? size : 0;
};

/**
 * Duration units, made distinguishable.
 *
 * A duration is a bare `number` everywhere in this codebase, and the two
 * units in play differ by 1000x. `formatDuration` takes SECONDS;
 * `Track.durationMs` and `SpaceTrackRow.durationMs` are MILLISECONDS.
 * Passing the wrong one renders a track as 1000x too long and throws
 * nothing — `formatDuration(212_000)` prints "3533:20" instead of "3:32".
 *
 * These brands make the two non-interchangeable. Both are subtypes of
 * `number`, so a branded value still works in arithmetic and comparisons;
 * only the direction that matters is blocked.
 *
 * Convert explicitly with `msToSeconds`. Mint with `asSeconds` only where
 * the value is known to be seconds already — an HTML media element's
 * `currentTime`/`duration`, or a comment timestamp.
 */
declare const SECONDS: unique symbol;
declare const MILLISECONDS: unique symbol;
export type Seconds = number & { readonly [SECONDS]: true };
export type Milliseconds = number & { readonly [MILLISECONDS]: true };

/** Assert that a number is already in seconds. */
export const asSeconds = (value: number): Seconds => value as Seconds;

/** Assert that a number is already in milliseconds. */
export const asMilliseconds = (value: number): Milliseconds =>
  value as Milliseconds;

/** The only correct way to feed a `durationMs` to `formatDuration`. */
export const msToSeconds = (value: Milliseconds | number): Seconds =>
  ((value as number) / 1000) as Seconds;

/**
 * Resolve the version file a track should play by default.
 *
 * The parameter is the structural minimum rather than `Track`, because this
 * is called with BOTH a canonical track and a row-backed tile (see the
 * client's `RenderableTrack`). A row-only tile carries `defaultVersionId`
 * projected from the row's `defaultVersionFileId` and no version array at
 * all, and the body below already tolerates every field being absent.
 *
 * `Track` remains assignable, so no existing caller changes.
 */
export const getDefaultVersionId = (track: {
  defaultVersionId?: Id;
  versionFilesV2?: Id[];
  pinnedVersionFiles?: Id[];
}) => {
  if (!track) return undefined;

  // Always use the explicit default version if it exists
  if (track.defaultVersionId) return track.defaultVersionId;

  // Otherwise, use the last pinned version, or the latest version,
  // if there are no pinned versions
  const trackVersions = track.versionFilesV2 || [];
  const pinnedVersions = track.pinnedVersionFiles || [];
  const lastOrderedPinnedVersion = pinnedVersions[pinnedVersions.length - 1];
  const latestVersion = trackVersions[trackVersions.length - 1];
  return lastOrderedPinnedVersion || latestVersion;
};

export const formatStripeDate = (date: number) =>
  moment(date * 1000).format("MMMM Do, YYYY");

export const getPlanStatus = (subscriptions: UserSubscriptions) => {
  if (!subscriptions?.active) {
    return "You are currently on the Free Plan.";
  }

  const active = parseSubscription(subscriptions.active);
  const scheduled = parseScheduledSubscription(subscriptions.scheduled);
  const cancelAt = subscriptions.active.cancel_at;

  const currentStatus = `You are currently on the ${
    STRIPE_INTERVAL_FORMATTED[active.interval]
  } ${SUBSCRIPTION_TIER_FORMATTED[active.tier]} Plan.`;

  if (!cancelAt) {
    return currentStatus;
  }

  const subscriptionDirection = getSubscriptionDirection(
    active.priceId,
    scheduled.priceId,
  );
  let verb = "switched";
  if (subscriptionDirection === SUBSCRIPTION_DIRECTION.UPGRADE) {
    verb = "upgraded";
  }
  if (subscriptionDirection === SUBSCRIPTION_DIRECTION.DOWNGRADE) {
    verb = "downgraded";
  }
  const upcomingPlan =
    scheduled.tier === SUBSCRIPTION_TIER.FREE
      ? "Free"
      : `${STRIPE_INTERVAL_FORMATTED[scheduled.interval]} ${
          SUBSCRIPTION_TIER_FORMATTED[scheduled.tier]
        }`;

  return `${currentStatus} On ${formatStripeDate(
    cancelAt,
  )}, you will automatically be ${verb} to the ${upcomingPlan} Plan.`;
};

export enum SUBSCRIPTION_DIRECTION {
  UPGRADE = 1,
  DOWNGRADE = -1,
  SWITCH = 0,
}

export const getSubscriptionDirection = (
  fromPriceId: string,
  toPriceId: string,
) => {
  const { tier: fromTier } = parsePriceId(fromPriceId);
  const { tier: toTier } = parsePriceId(toPriceId);
  const fromIndex = SUBSCRIPTION_TIER_HIERARCHY.indexOf(fromTier);
  const toIndex = SUBSCRIPTION_TIER_HIERARCHY.indexOf(toTier);
  if (fromIndex < toIndex) return SUBSCRIPTION_DIRECTION.UPGRADE;
  if (fromIndex > toIndex) return SUBSCRIPTION_DIRECTION.DOWNGRADE;
  return SUBSCRIPTION_DIRECTION.SWITCH;
};

export const parseSubscriptionFromRCEntitlement = (
  entitlement?: RevenueCatEntitlement,
): {
  tier: SUBSCRIPTION_TIER;
  provider?: REVENUECAT_PRODUCT_PROVIDER;
  interval?: SUBSCRIPTION_INTERVAL;
} => {
  const identifier =
    // we first look at the product_plan_identifier, which is populated only
    // for subscriptions purchased via Google Play Store.
    entitlement?.product_plan_identifier || entitlement?.product_identifier;
  return (
    REVENUECAT_PRODUCT_ID_TO_SUBSCRIPTION[identifier] || {
      tier: SUBSCRIPTION_TIER.FREE,
    }
  );
};

export const getPrevInArray = <T>(item: T, array: T[]) => {
  if (!item) return;
  const currentIndex = array.indexOf(item);
  if (currentIndex < 0) return;
  return array[currentIndex - 1];
};

export const getNextInArray = <T>(item: T, array: T[]) => {
  if (!item) return;
  const currentIndex = array.indexOf(item);
  if (currentIndex < 0) return;
  return array[currentIndex + 1];
};

export const getFileExtension = (fileName: string) => {
  const fileParts = fileName.toLowerCase().split(".");
  return fileParts[fileParts.length - 1];
};

export const isLossless = (fileName: string) => {
  const ext = getFileExtension(fileName);
  return ["flac", "wav", "aif", "aiff"].includes(ext);
};

export const getQualityOptionsForFile = (fileName: string) => {
  return audioQualityOptions.filter((option) => {
    if (option.quality !== AUDIO_QUALITY.ORIGINAL) return true;
    if (isLossless(fileName) && option.format === "flac") return true;
    if (!isLossless(fileName) && option.format === "mp3") return true;
    return false;
  });
};

export const isFilePlayable = (file: FileEntity) => {
  if (!file) return false;
  if (file.processingErrorV3) return false;
  if (!file.isProcessedV3) return false;
  if (!file.metadata?.duration) return false;
  return true;
};

export const FILE_NOT_FOUND_ERROR =
  "File not found. It may have been deleted or moved.";

export const getApiRoot = () => {
  return process.env.DEPLOY_ENV === "local" || process.env.CI === "1"
    ? `http://127.0.0.1:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1`
    : `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
};

export const getEntityTypeName = (id: COLLECTION_ID) => {
  if (id === COLLECTION_ID.COMMENT) return "Comment";
  if (id === COLLECTION_ID.TRACK) return "Track";
  if (id === COLLECTION_ID.SPACE) return "Space";
  if (id === COLLECTION_ID.FILE) return "File";
  if (id === COLLECTION_ID.USER) return "User";
  return "Item";
};

export const getEntitySubscribers = (entity: Space, role = ROLE.VIEW) => {
  const roles = getFullRoles(entity);
  const subscribers = Object.keys(roles).filter(
    (id) => id !== PUBLIC_ID && hasRole(id, role, entity),
  );
  return subscribers;
};

export const isEntityPasswordProtected = (entity: Space): boolean => {
  return (
    (entity.subscriptionTier === SUBSCRIPTION_TIER.PRO ||
      entity.subscriptionTier === SUBSCRIPTION_TIER.STUDIO ||
      entity.subscriptionTier === SUBSCRIPTION_TIER.INDIE) &&
    // we only look at if share password is enabled for the space because
    // we temporarily disabled public share link completely.
    entity.sharePasswordEnabled
  );
};

export const getEntityShareKeys = (entity: Space) => {
  const entityRoles = entity?.rolesV2 || {};

  // Get all the valid share keys (must have roles attached).
  const shareKeys = Object.keys(entityRoles).filter(
    (key) => isShareId(key) && !!entityRoles[key],
  );

  return shareKeys;
};

/**
 * `getEntityShareKeys` treats `rolesV2.share_x: []` (the key present, its
 * roles array EMPTY) as a valid share key — `!![] === true`, so mere
 * presence of the key is enough regardless of whether it actually grants
 * anything. That's the right predicate for most of its callers
 * (password/visibility gating just needs "is there a share-key slot"), but
 * it's the WRONG one for "does this space currently grant access via a
 * share link": `updateSpace.ts`'s `rolesUpdateSchema` has no `.min(1)` on
 * the roles array, and that same file's own `isActivating` check already
 * treats `value?.length === 0` as NOT activating — i.e. the codebase
 * already has a second, ACTIVE-vs-merely-PRESENT distinction for share
 * keys. This is that distinction, exposed as a reusable predicate instead
 * of a local one-off, for anything that needs "is a share link actually
 * live" (e.g. the track-sharing plan §4.5 revocation sweep).
 *
 * Deliberately does NOT delegate to `getEntityShareKeys` above — kept as an
 * independent filter over `entityRoles` so the two predicates can never
 * drift into silently depending on each other's exact filter order.
 */
export const getActiveEntityShareKeys = (entity: Space) => {
  const entityRoles = entity?.rolesV2 || {};

  return Object.keys(entityRoles).filter(
    (key) => isShareId(key) && (entityRoles[key] || []).length > 0,
  );
};

const getEntityShareRoles = (entity: Space) => {
  const [shareKey] = getEntityShareKeys(entity);
  const entityRoles = entity?.rolesV2 || {};
  const shareRoles = entityRoles[shareKey] || [];
  return new Set(shareRoles);
};

export const spaceHasRolesCheck = (entity: Space, roles: ROLE[]) => {
  const shareRoles = getEntityShareRoles(entity);
  const hasRoles = roles.every((role) => shareRoles.has(role));
  return hasRoles;
};

export const getUserContactMethods = (user: Partial<UserEntity>) => {
  const emails: string[] = [];
  const phones: string[] = [];

  if (user) {
    const linkedAccountsV2 = user.linkedAccountsV2 || [];

    if (user.connection === "sms") phones.push(user.phone);
    else emails.push(user.email);

    linkedAccountsV2.forEach((acct) => {
      if (acct.connection === "sms") phones.push(acct.identifier);
      else emails.push(acct.identifier);
    });
  }

  return {
    email: emails[0],
    phone: phones[0],
  };
};

export const isActiveEntity = (entity: Space) => {
  if (!entity) return false;
  if ((entity as Space).isArchived) return false;

  const roles = getFullRoles(entity);
  let isActive = false;

  Object.entries(roles).forEach(([id, permissions]) => {
    // If this rule is just to block access, it doesn't count
    if (permissions.length < 1) return;

    // If the user is the owner, it doesn't count
    if (id === entity.createdBy) return;

    isActive = true;
  });

  return isActive;
};

export const isSpacePrivateInboxEnabled = (space: Space) => {
  return isSpacePrivateInboxToggleable(space) && space?.privateInboxEnabled;
};

export const isSpaceAudioQualityLocked = (space: Space) => {
  return (
    space?.subscriptionTier === SUBSCRIPTION_TIER.STUDIO &&
    space?.audioQualityLocked
  );
};

// a dropbox job is considered a stale job if it's in progress
// but hasn't been updated in 30 seconds
export const isDropboxJobStale = (
  job?: DropboxSyncJob,
  states = [JOB_STATUS.IN_PROGRESS],
) => {
  if (!job || !states.includes(job.status)) {
    return false;
  }
  const elapsed = Date.now() - job.updatedAt;
  return elapsed > 30000;
};

export const isSpacePrivateInboxToggleable = (space: Space) => {
  return (
    AppFeaturesStatus[APP_FEATURES.PRIVATE_INBOX] &&
    space?.experimentalPrivateInboxEnabled
  );
};

export class OperationTimeoutError extends Error {
  constructor(message: string) {
    const errMessage = `OperationTimeoutErr: ${message}`;
    super(errMessage);

    // `Error` class needs a proper prototype chain setup to work correctly with `instanceof`.
    Object.setPrototypeOf(this, OperationTimeoutError.prototype);
  }
}

export const timer = (timeoutMs: number, errMessage: string) => {
  return new Promise((_, reject) => {
    if (timeoutMs < 0) {
      reject(new Error("Invalid timeout value"));
    }
    const signal = AbortSignal.timeout(timeoutMs);
    signal.addEventListener("abort", () => {
      reject(new OperationTimeoutError(errMessage));
    });
  });
};

export const checkIsUserAnonymous = (userId: string) => {
  // user is anonymous if the ID has no | in it
  // user is authenticated if the ID is in {login_method}|random_string format
  return !userId?.includes("|");
};

export const EXISTING_AUTH_WITH_GOOGLE_ERR =
  'You signed in differently before. Try "Continue with Google Account."';

export const EXISTING_AUTH_WITH_APPLE_ERR =
  'You signed in differently before. Try "Continue with Apple."';

export const MAX_ACTIVE_SPACES_ERROR =
  "User has the maximum number of Shared Spaces included in their account.";

export const MAX_STORAGE_SPACE_ERROR =
  "User has reached the maximum storage space limt in their account.";

export const MAX_TRACKS_ERROR =
  "User has the maximum number of tracks included in their account.";

export const DUPLICATE_FILE_REFERENCE_ERROR =
  "Some files are already used in another space or track.";

export const SPACE_NAME_ALREADY_EXISTS_ERROR =
  "A space with this name already exists.";

export const TRACK_NAME_ALREADY_EXISTS_ERROR =
  "A track with this title already exists in this space.";

export const ATTACHMENT_NAME_ALREADY_EXISTS_ERROR =
  "The name is being used by another attachment.";

export const TRACK_VERSION_NAME_ALREADY_EXISTS_ERROR =
  "The name is being used by another version.";

export const sortItemsByItemsOrder = <T extends { id: Id; entity: Entity }>(
  itemsOrder: string[],
  items: T[],
) => {
  const itemsOrderMap = itemsOrder.reduce<Record<string, boolean>>(
    (acc, itemId) => {
      acc[itemId] = true;
      return acc;
    },
    {},
  );
  const itemsMap = items.reduce<Record<string, T>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
  const orderedItems = itemsOrder.reduce((acc, itemId) => {
    if (itemsMap[itemId]) {
      acc.push(itemsMap[itemId]);
    }
    return acc;
  }, []);
  const unOrderedItems = items.filter((item) => {
    return !itemsOrderMap[item.id];
  });
  return {
    orderedItems,
    unOrderedItems,
  };
};

export const sanitizeUserDataForSegment = (
  traits: object & {
    dropboxTokens?: DropboxAuthTokens;
    createdAt?: number;
    storageUsed?: number;
    requestedAccountDeletionAt?: number;
  },
) => {
  if (!traits) {
    return traits;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dropboxTokens, ...userTraits } = traits;
  return {
    ...userTraits,
    ...(typeof userTraits?.requestedAccountDeletionAt === "number" && {
      requestedAccountDeletionAt_formatted: new Date(
        userTraits.requestedAccountDeletionAt,
      ).toISOString(),
    }),
    ...(typeof userTraits?.createdAt === "number" && {
      createdAt_formatted: new Date(userTraits.createdAt).toISOString(),
    }),
    ...(typeof userTraits?.storageUsed === "number" && {
      // convert bytes to megabytes
      storageUsed_formatted:
        userTraits.storageUsed > 0
          ? parseFloat((userTraits.storageUsed / (1024 * 1024)).toFixed(19))
          : userTraits.storageUsed,
    }),
  };
};

export const GUEST_MODE_DISABLED_ERR_MESSAGE =
  "Guest mode is disabled for this resource.";

/**
 * Shape of the `details` payload attached to the guest-mode-disabled
 * (`GUEST_MODE_DISABLED_ERR_MESSAGE`) error thrown by `checkProtectedEntity`.
 * Clients render this as the pre-join ("join to view") preview. The first
 * three fields are always present; the rest are additive/enrichment fields
 * and may be null/absent, so consumers must tolerate that.
 */
export type GuestModeDisabledDetails = {
  owner: string;
  spaceName: string;
  spaceArtworkUrl?: string | null;
  description?: string | null;
  creatorName?: string | null;
  creatorAvatarUrl?: string | null;
  memberCount?: number | null;
  memberAvatarUrls?: string[];
  countdownFrom?: number | null;
};

export const INBOX_PERMISSION_DENIED_ERR_MESSAGE =
  "You don't have permission to access this inbox.";

export const AUDIO_UPLOAD_LIMIT_ERR_MESSAGE =
  "This file size exceeds our audio upload limit of 2GB. Select a smaller audio file to continue.";

export class DropboxManagerBase {
  static redirectToSessionStorageKey = "dropbox_redirect_to";
  static fileSizeLimitExeedsErrorMsg = AUDIO_UPLOAD_LIMIT_ERR_MESSAGE;
  static storageLimitExceedsErrorMsg = "You have exceeded the storage limit.";

  clientId = process.env.REACT_APP_DROPBOX_CLIENT_ID;
  fetcher: (url: string) => Promise<NodeFetchResponse | Response>;

  static getTooManyFilesErrorMsg(
    subscriptionTier: SUBSCRIPTION_TIER = SUBSCRIPTION_TIER.FREE,
  ) {
    return `You can only import up to ${DropboxManagerBase.getNumberOfFilesLimit(subscriptionTier)} files at a time.`;
  }

  static getNumberOfFilesLimit(
    subscriptionTier: SUBSCRIPTION_TIER = SUBSCRIPTION_TIER.FREE,
  ) {
    return [
      SUBSCRIPTION_TIER.INDIE,
      SUBSCRIPTION_TIER.PRO,
      SUBSCRIPTION_TIER.STUDIO,
    ].includes(subscriptionTier)
      ? 25
      : 15;
  }

  private static validateFileMetadata(
    metadata: files.FileMetadataReference,
    contentType: string | null,
  ) {
    // make sure the file is less than 2GB
    if (metadata.size > AUDIO_UPLOAD_SIZE_LIMIT_IN_BYTES) {
      throw new Error(this.fileSizeLimitExeedsErrorMsg);
    }
    // make sure that the file type is what we support
    if (!AUDIO_ACCEPT_TYPES.includes(contentType)) {
      throw new Error(`${contentType} is not supported.`);
    }
  }

  constructor({
    fetcher,
  }: {
    fetcher: (url: string) => Promise<NodeFetchResponse | Response>;
  }) {
    this.fetcher = fetcher;
  }

  private getDropbox(accessToken: string) {
    return new Dropbox({
      clientId: this.clientId,
      fetch: this.fetcher,
      accessToken,
    });
  }

  // instance that extends this class should implement this method
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected reportAndThrowError(err: Error, messagePrefix?: string) {
    throw err;
  }

  async getFileMetadataForDownload(
    accessToken: string,
    fileId: string,
  ): Promise<{
    metadata: files.FileMetadataReference;
    fileFetchResponse: NodeFetchResponse | Response;
  }> {
    const metadata = await this.getFileMetadata(accessToken, fileId);
    const tempLink = await this.getFileTemporaryLink(accessToken, fileId);
    const resp = await this.fetcher(tempLink);
    const contentType = resp.headers.get("content-type");
    DropboxManagerBase.validateFileMetadata(metadata, contentType);
    return {
      metadata,
      fileFetchResponse: resp,
    };
  }

  async listFolder(
    accessToken: string,
    args: files.ListFolderArg,
  ): Promise<files.ListFolderResult> {
    try {
      const db = this.getDropbox(accessToken);
      const response = await db.filesListFolder(args);
      if (!response.result) {
        throw new Error("No result from Dropbox API.");
      }
      return response.result;
    } catch (err) {
      this.reportAndThrowError(
        err,
        "Error getting folder contents from Dropbox",
      );
    }
  }

  async getFileTemporaryLink(accessToken: string, fileId: string) {
    try {
      const db = this.getDropbox(accessToken);
      const response = await db.filesGetTemporaryLink({ path: fileId });
      if (!response?.result?.link) {
        throw new Error("No result from Dropbox API.");
      }
      return response.result.link;
    } catch (err) {
      this.reportAndThrowError(
        err,
        "Error getting file temporary link from Dropbox",
      );
    }
  }

  async getFileMetadata(accessToken: string, fileId: string) {
    try {
      const db = this.getDropbox(accessToken);
      const metadata = await db.filesGetMetadata({ path: fileId });
      // in our workflow, we should only be calling this API for an individual file
      // if response indicates that it is not a file, we should consider it an error.
      if (!metadata.result || metadata.result[".tag"] !== "file") {
        throw new Error("No result from Dropbox API.");
      }
      return metadata.result as files.FileMetadataReference;
    } catch (err) {
      this.reportAndThrowError(
        err,
        "Error getting files metadata from Dropbox",
      );
    }
  }
}

/**
 * This regex validates URLs, including those with localhost, ports, and query/hash parameters.
 * It is used to validate the redirect URL field in the Space Settings.
 */
export const isValidUrl = (url: string) =>
  /^(https?:\/\/)+([\da-z.-]+)(\.(localhost|\.[a-z.]{2,6}))?(:\d{1,5})?(\/[\w .-]*)*\/?(\?[^#]*)?(#.*)?$/.test(
    url,
  );

/**
 * Returns the maximum number of pinned versions for a given subscription tier.
 * @param subscriptionTier - The subscription tier to get the limit for.
 * @returns The maximum number of pinned versions for the given subscription tier.
 */
export const getPinnedVersionsLimit = (
  subscriptionTier?: SUBSCRIPTION_TIER, // entity subscription tier
) => {
  const limit =
    // If the subscription tier is not provided or is free, use the free limit.
    // TODO: Ideally, we should check `!subscriptionTier || subscriptionTier === SUBSCRIPTION_TIER.FREE`,
    // but currently, checking `!subscriptionTier` causes an error for entities that don't have a subscription tier saved.
    !subscriptionTier || subscriptionTier === SUBSCRIPTION_TIER.FREE
      ? MAX_PINNED_VERSIONS_FREE
      : MAX_PINNED_VERSIONS;

  return limit;
};

/**
 * Returns the limited pinned versions for a given subscription tier.
 * @param pinnedVersions - The pinned versions to limit.
 * @param subscriptionTier - The space subscription tier to get the limit for.
 * @returns The limited pinned versions for the given space subscription tier of the entity.
 */
export const getLimitedPinnedVersions = (
  pinnedVersions: string[],
  subscriptionTier?: SUBSCRIPTION_TIER, // entity subscription tier
): string[] => {
  const limit = getPinnedVersionsLimit(subscriptionTier);

  if (pinnedVersions.length <= limit) {
    return pinnedVersions;
  }

  // Always take the most recent pins by slicing from the end
  return pinnedVersions.slice(-limit);
};

/**
 * The composite Firestore document id for a `space-tracks-v1` row —
 * `${spaceId}_${trackId}` (track-sharing plan §1.2). This id format is
 * load-bearing in `firestore.rules`, which reflects it directly, so it must
 * have exactly one definition. Lives here — not in `core/db/spaceTracks.ts`,
 * which imports `firebase-admin/firestore` and `crypto` and so is not
 * client-importable — so both the server and the web client can compute the
 * same id without duplicating the format. `core/db/spaceTracks.ts`
 * re-exports this for existing server callers.
 */
export const getSpaceTrackRowId = (spaceId: Id, trackId: Id): Id =>
  `${spaceId}_${trackId}`;

export const areSetsEqual = <T = string>(setA: Set<T>, setB: Set<T>) => {
  if (setA.size !== setB.size) {
    return false;
  }
  return Array.from(setA).every((item) => setB.has(item));
};
